import { PageHero, SectionTitle, StatCounter } from "@/components/Shared";
import { Reveal, StaggerGroup, StaggerItem } from "@/components/Reveal";
import { EditorialMarquee } from "@/components/Marquee";
import { Recycle, Shield, Flag, MapPin, Users, Timer, Check } from "lucide-react";
import { IMAGES, STATS, TRACKS } from "@/data/site";

const specs = [
  { icon: MapPin, t: "150,000+ Sq Ft", d: "Our circuit is laid out across the Southern Parking Lot of the Thomas A. Robinson National Stadium in Nassau." },
  { icon: Recycle, t: "Recycled Tyre Walls", d: "Every corner is shaped and protected by hundreds of recycled used car tyres — resourceful, sustainable and real." },
  { icon: Flag, t: "Real Wheel-To-Wheel", d: "Genuine racing lines, overtaking and grid battles — this is racing, not a slow theme-park ride." },
  { icon: Shield, t: "Safety First", d: "Helmets and a full driver briefing for everyone, with marshals watching every session." },
  { icon: Users, t: "Two Tracks", d: "A larger main circuit for adults and a dedicated junior track so kids can race too." },
  { icon: Timer, t: "Kart Timing — Coming Soon", d: "A full kart timing system is on the way. Soon you'll track every lap and battle for the fastest time on the board." },
];

export default function Experience() {
  return (
    <div data-testid="experience-page">
      <PageHero
        eyebrow="THE EXPERIENCE"
        title="Real Racing, Real Grit"
        subtitle="A hand-built, tyre-lined circuit on a stadium parking lot — the closest thing to true motorsport in The Bahamas right now."
        image={IMAGES.racingAction}
        testid="experience-hero"
      />

      <section className="bg-[#050505] py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <SectionTitle eyebrow="WHAT MAKES IT SPECIAL" title="Grassroots, Done Right" className="mb-16" />
          <StaggerGroup className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {specs.map((s) => (
              <StaggerItem key={s.t}>
                <div className="group h-full rounded-xl border border-white/10 bg-obsidian p-8 transition-all hover:-translate-y-2 hover:border-kbcyan/50">
                  <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-kbcyan/10 text-kbcyan transition-colors group-hover:bg-kbcyan group-hover:text-black">
                    <s.icon className="h-7 w-7" />
                  </div>
                  <h3 className="mt-6 font-display text-3xl tracking-wide text-white">{s.t}</h3>
                  <p className="mt-3 leading-relaxed text-white/55">{s.d}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* Two tracks */}
      <section data-testid="two-tracks-section" className="bg-obsidian py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <SectionTitle eyebrow="ONE VENUE · TWO TRACKS" title="A Track For Everyone" className="mb-16" />
          <div className="grid gap-8 md:grid-cols-2">
            {TRACKS.map((t, i) => (
              <Reveal key={t.name} delay={i * 0.12}>
                <div className="group flex h-full flex-col overflow-hidden rounded-xl border border-white/10 bg-[#0A0A0A]">
                  <div className="relative h-72 overflow-hidden">
                    <img src={t.image} alt={t.name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />
                    <span className="absolute left-5 top-5 rounded-full bg-kbyellow px-3 py-1 text-xs font-semibold tracking-wide text-black">{t.tag}</span>
                  </div>
                  <div className="flex flex-1 flex-col p-8">
                    <h3 className="font-display text-4xl tracking-wide text-white">{t.name}</h3>
                    <p className="mt-3 leading-relaxed text-white/55">{t.desc}</p>
                    <ul className="mt-6 grid grid-cols-2 gap-3">
                      {t.specs.map((sp) => (
                        <li key={sp} className="flex items-start gap-2 text-sm text-white/70">
                          <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-kbcyan" />
                          {sp}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden">
        <img src={IMAGES.trackDrone} alt="Tyre-lined circuit" className="h-[70vh] w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-black/50 to-transparent" />
        <div className="absolute inset-0 flex items-center">
          <div className="mx-auto w-full max-w-[1400px] px-5 md:px-10">
            <Reveal>
              <span className="font-display text-lg tracking-[0.3em] text-kbcyan">THE CIRCUIT</span>
              <h2 className="mt-3 max-w-xl font-display text-5xl leading-[0.9] tracking-tight text-white md:text-7xl">
                Lined Corner To Corner In Tyres
              </h2>
              <p className="mt-6 max-w-md leading-relaxed text-white/70">
                Hundreds of recycled used car tyres form the barriers and define every bend of the layout.
                It's raw, it's real, and it races hard.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-obsidian py-16 md:py-20">
        <div className="mx-auto grid max-w-[1400px] grid-cols-2 gap-10 px-5 md:grid-cols-4 md:px-10">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.08}><StatCounter {...s} /></Reveal>
          ))}
        </div>
      </section>

      <EditorialMarquee words={["ARRIVE", "SIGN", "GEAR UP", "RACE", "REPEAT"]} />
    </div>
  );
}
